import { css } from '@emotion/react'
import { patchContact, remindContactInvite, useSentContactReminders } from 'api/goodtrust/contact'
import { isMfaDisabledTemporarily, isReminderSMSDisabled } from 'api/goodtrust/user'
import { Button } from 'components/button/Button'
import { FormButton, FormFieldset } from 'components/FormButton'
import { InputGrid } from 'components/Grid'
import { Icon } from 'components/icon'
import { CustomPhoneInput } from 'components/inputs/phoneInput/PhoneInput'
import { BlockSpinner } from 'components/spinner/Spinner'
import { handleAndToastError, toastSuccess } from 'components/Toast'
import { Text, Title } from 'components/Typography'
import { intervalToDuration } from 'date-fns'
import { useTranslation } from 'next-i18next'
import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { isPossiblePhoneNumber } from 'react-phone-number-input'
import { colors } from 'utils/colors'
import { MessageException } from 'utils/error'
import { unwrapResponse } from 'utils/fetcher'
import { useInterval } from 'utils/hooks'
import { useDynamicTranslations } from 'utils/i18n/useDynamicTranslations'
import { pxToRem } from 'utils/styled'
import { ApiTrustedContactResponse, GenericFunction } from 'utils/types'
import { SDialogModal, SForm } from './ContactInvitedModal.styled'

const getDuration = (date?: string | null) => {
  if (!date) return null
  const end = new Date(date)
  end.setDate(end.getDate() + 1)
  return intervalToDuration({
    start: new Date(),
    end,
  })
}

interface FormFields {
  phone: string
}

export const ContactInvitedModalConfig = {
  modalCss: css`
    max-width: ${pxToRem(580)}rem;
  `,
}

export const ContactInvitedModal = (props: {
  close: GenericFunction
  onSuccess?: GenericFunction
  contact?: ApiTrustedContactResponse
  isReinvited?: boolean
  isLimited?: boolean
}) => {
  const { t, isLoading } = useDynamicTranslations('trusted_contact')
  const { t: tCommon } = useTranslation()
  const { contact } = props

  const { data: sentReminders } = useSentContactReminders(contact?.uuid)
  const [remainingTime, setRemainingTime] = useState(
    getDuration(sentReminders?.json?.last_email_notification)
  )

  const form = useForm<FormFields>({
    defaultValues: {
      phone: contact?.contact_user?.phone_number ?? '',
    },
  })
  const onSubmit = async (data: FormFields) => {
    try {
      if (!contact?.uuid) {
        throw new MessageException('something_went_wrong')
      }
      await patchContact({ phone_number: data.phone }, { uuid: contact.uuid }).then(unwrapResponse)
      await remindContactInvite(contact.uuid, contact.user?.type ?? undefined, {
        send_type: 'SMS',
      }).then(unwrapResponse)
      await props.onSuccess?.()
      toastSuccess('sms_reminder_sent')
      props.close()
    } catch (err) {
      handleAndToastError(err)
    }
  }

  useInterval(
    () => setRemainingTime(getDuration(sentReminders?.json?.last_email_notification)),
    props.isLimited ? 1000 : null
  )

  if (isLoading) {
    return <BlockSpinner />
  }
  return (
    <SDialogModal>
      {props.isLimited ? (
        remainingTime ? (
          <>
            <Icon name="TimeClock" color={colors.blue[500]} />
            <Title variant="h7">
              {t('trusted_contact.contacts.email_reminders_reached', {
                hours: remainingTime.hours,
                minutes: remainingTime.minutes,
                seconds: remainingTime.seconds,
              })}
            </Title>
            <Text>
              {contact?.user?.type === 'LEGACY_CONTACT'
                ? t('trusted_contact.contacts.contact_invited_modal_desc_legacy')
                : t('trusted_contact.contacts.contact_invited_modal_desc_trusted')}
              {!isMfaDisabledTemporarily() && !isReminderSMSDisabled() && (
                <>
                  {contact?.user?.type === 'LEGACY_CONTACT'
                    ? t('trusted_contact.contacts.contact_invited_modal_send_sms_legacy')
                    : t('trusted_contact.contacts.contact_invited_modal_send_sms_trusted')}
                </>
              )}
            </Text>
          </>
        ) : (
          <BlockSpinner />
        )
      ) : (
        <>
          <Icon name="Check" color={colors.green[200]} size="3rem" />
          {props.isReinvited ? (
            <>
              <Title variant="h7">
                {t('trusted_contact.contacts.reminder_email_sent')}
                {!isMfaDisabledTemporarily() && !isReminderSMSDisabled() && (
                  <>
                    <br />
                    {t('trusted_contact.contacts.enter_phone_number_for_reminder')}
                  </>
                )}
              </Title>
              <Text>
                {contact?.user?.type === 'LEGACY_CONTACT'
                  ? t('trusted_contact.contacts.invitation_waiting_text_legacy')
                  : t('trusted_contact.contacts.invitation_waiting_text_trusted')}
                {!isMfaDisabledTemporarily() && !isReminderSMSDisabled() && (
                  <>
                    {contact?.user?.type === 'LEGACY_CONTACT'
                      ? t('trusted_contact.contacts.send_reminder_sms_text_legacy')
                      : t('trusted_contact.contacts.send_reminder_sms_text_trusted')}
                  </>
                )}
              </Text>
            </>
          ) : (
            <>
              <Title variant="h7">
                {contact?.user?.type === 'LEGACY_CONTACT'
                  ? t('trusted_contact.contacts.invitation_sent_legacy')
                  : t('trusted_contact.contacts.invitation_sent_trusted')}
              </Title>
              <Text>
                {contact?.user?.type === 'LEGACY_CONTACT'
                  ? t('trusted_contact.contacts.invitation_sent_legacy_2')
                  : t('trusted_contact.contacts.invitation_sent_trusted_2')}
              </Text>
              <Text>
                {isMfaDisabledTemporarily() || isReminderSMSDisabled()
                  ? t('trusted_contact.contacts.invitation_sent_no_sms_reminder')
                  : contact?.user?.type === 'LEGACY_CONTACT'
                  ? t('trusted_contact.contacts.invitation_sent_sms_reminder_legacy')
                  : t('trusted_contact.contacts.invitation_sent_sms_reminder_trusted')}
              </Text>
            </>
          )}
        </>
      )}
      {!isMfaDisabledTemporarily() && !isReminderSMSDisabled() && (
        <SForm noValidate onSubmit={form.handleSubmit(onSubmit)}>
          <InputGrid>
            <Controller
              name="phone"
              rules={{
                validate: (value) => {
                  if (value && isPossiblePhoneNumber(value as string)) {
                    return true
                  } else {
                    return tCommon('common.form.invalid_phone')
                  }
                },
              }}
              control={form.control}
              render={({ field }) => (
                <FormFieldset name="phone" form={form}>
                  <CustomPhoneInput {...field} />
                </FormFieldset>
              )}
            />
          </InputGrid>
          <FormButton form={form}>
            <Button type="submit" size="large">
              {props.isReinvited
                ? t('trusted_contact.contacts.contact_invited_modal_reinvite_button')
                : t('trusted_contact.contacts.contact_invited_modal_invite_button')}
            </Button>
          </FormButton>
        </SForm>
      )}
    </SDialogModal>
  )
}
